import {colors} from "@c10h/colors";
import {Trans, useTranslation} from "ni18n";
import React from "react";
import {useTypedSelector} from "src/store";
import {useV5ImgSrc} from "src/useV5ImgSrc";

import {Alink} from "../../_common/Carbon";
import {LazyComp} from "../../_common/LazyComp";
import {Btn} from "../V5Comp";

type Props = {
  nextApptSlot: string;
  bookingUrl: string;
  bookingBtnHandler: () => void;
};

const Features: React.FC<Props> = ({nextApptSlot, bookingUrl, bookingBtnHandler}) => {
  const i18n = useTranslation();
  const {isMobile} = useTypedSelector(state => state.config);
  const bgImageSrc = useV5ImgSrc("woman-with-coffee-xl");

  const data = [
    {
      icon: "virtual-phone",
      title: i18n.t("Unlimited virtual visits on your schedule"),
      body: i18n.t(
        "Carbon Health clinicians administer care and provide COVID-19 clearance from the comfort of your home. You can rest easy knowing our COVID medical experts will be with you until you're well.",
      ),
    },
    {
      icon: "medical-record",
      title: i18n.t("Customized care plans"),
      body: i18n.t(
        "Whether you’re asymptomatic or navigating a more complex situation, providers will customize a plan that makes sense for you.",
      ),
    },
    {
      icon: "symptom-checker-phone2",
      title: i18n.t("Care on demand"),
      body: i18n.t(
        "Receive customized symptom tracking and daily monitoring via our clinician teams. Depending on symptoms, we will provide over-the-counter or prescription therapies as well as guidance on symptom management.",
      ),
    },
    {
      icon: "vitals",
      title: i18n.t("Access to medical COVID experts"),
      body: i18n.t(
        "We can help triage any short or long-term complications that may arise and, in some cases, will provide pulse oximeters for self-monitoring at no extra cost.",
      ),
    },
    {
      icon: "mental-health",
      title: i18n.t("Mental health screenings & referrals"),
      body: i18n.t(
        "Not all COVID symptoms are physical; A positive COVID-19 test also affects your mental health. We will screen you for mental health symptoms and refer you to the proper providers if desired.",
      ),
    },
    {
      icon: "virtual-care-screen",
      title: i18n.t("Full health support"),
      body: (
        <Trans>
          Not only will you have full access to our team of providers, but you will also have access
          to digital therapeutics like Big Health & Sleepio as well as our{" "}
          <Alink href="/get-care/covid-treatment" className="tdu">
            resource-rich education hub
          </Alink>
          .
        </Trans>
      ),
    },
  ];

  return (
    <div className="pv30 pb20-md pt10-md ph10 ph4-sm bg-darkerGreen pos-r">
      {!isMobile && (
        <LazyComp>
          <div
            className="pos-a h100p w100p dn-md bgcov top0 right0"
            style={{
              backgroundImage: `url(${bgImageSrc})`,
              backgroundPosition: "75% 0%",
            }}
          >
            <div
              className="h100p w100p"
              style={{
                background: "linear-gradient(90deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%",
              }}
            />
          </div>
        </LazyComp>
      )}
      <div className="maxw80 ma zIndex2 pos-r pt40 pt30-xl pt10-md">
        <h2 className="gray50 maxw35 mb48 mb40-xl mb30-lg mb8-md pr0-md">
          <Trans>
            Every COVID-19 experience is unique. Our team of expert clinicians works to create a{" "}
            <span className="lightGreen">
              customized care plan that makes sense for each patient's unique health needs.
            </span>
          </Trans>
        </h2>
        <h5 className="lightGreen font-mr fs13 fs12-sm mt20 mt8-md mb10-md">
          {i18n.t("With COVID Positive Care you get:")}
        </h5>
        <ul className="mt8 dg gg6 maxw72 af" style={{"--w": "305px"}}>
          {data.map(d => (
            <li
              key={d.icon}
              className="bg-white br8 p10 pt14 p8-md p6-xs df fdc aifs tal maxw24 minw0"
            >
              <span className={`cIcon-${d.icon} fs56 lightGreen fs46-md`} />
              <div className="mt6 mt2-sm fs24 font-csb lh14 darkerGreen minh16 minh0-sm">
                {d.title}
              </div>

              <p className="mt4 mt2-sm font-ir-sm fs18">{d.body}</p>
            </li>
          ))}
        </ul>

        <div className="mt14 dgc aic gg4 jcs gtcn-sm mt16-sm">
          <Btn
            fontSize={1}
            bg={colors.darkGreen}
            hoverBg={colors.green}
            href={bookingUrl}
            onClick={bookingBtnHandler}
          >
            {i18n.t("Talk to a Virtual Provider")}
          </Btn>
          <div className="gray50 tal">
            {nextApptSlot && <div className="fs18 fs16-sm font-csb">{nextApptSlot}</div>}
            <div className="mt2 fs14 fs12-sm font-ir">{i18n.t("$0 to enroll")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
